// angular
import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  afterNextRender,
  Component,
  effect,
  inject,
  OnInit,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationStart,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
// components
import { ButtonComponent } from '@app/core/components/button/button.component';
import { MegaMenuComponent } from '../mega-menu/mega-menu.component';
import { ProfileMenuComponent } from '../profile-menu/profile-menu.component';
import { HomeSearchSectionComponent } from '@app/+home/components/home-search-section/home-search-section.component';
import { BusinessAccountDialogComponent } from '@core/dialogs/business-account-dialog/business-account-dialog.component';
import { SignupDialogComponent } from '@app/core/dialogs/signup-dialog/signup-dialog.component';
// services
import { LayoutService } from '@app/layout/shared/services/layout.service';
import { DialogService } from '@app/core/shared/services/dialog.service';
import { UserService } from '@app/shared/services/user.service';
import { CategoriesService } from '@app/+categories/shared/services/categories.service';
// types & envs
import { HeaderMenu } from '@app/shared/types/header-menu.type';
import { IUser } from '@app/shared/types/user.type';
import { ButtonsMenuTypes } from '@app/shared/types/languages.type';
import { CategoriesType } from '@app/+categories/shared/types/categories.type';
// utilities & constants
import { languagesData } from '@app/shared/constants/languages';
import { headerMenuData } from '@app/shared/constants/header-menu-data.const';
// pipes & enums
import { HasAccessPipe } from '@app/shared/pipes/has-access.pipe';
import { Roles } from '@app/shared/enums/roles.enum';
import { AuthService } from '@shared/services/auth.service';
import { MatomoTrackClickDirective } from 'ngx-matomo-client';

@Component({
  selector: 'pwj-header',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    NgOptimizedImage,
    MegaMenuComponent,
    ProfileMenuComponent,
    RouterLink,
    RouterLinkActive,
    TranslateModule,
    NgxSkeletonLoaderModule,
    HomeSearchSectionComponent,
    MatomoTrackClickDirective,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit {
  public menu: HeaderMenu[] = headerMenuData;
  public languagesMenuItems: ButtonsMenuTypes[] = languagesData;
  public selectedLang!: string;
  public activeRoute: string = '';
  public user!: IUser;
  public hiddenMegaMenu: boolean = true;
  public hiddenAdvancedFilters: boolean = true;
  public hiddenProfileMenu: boolean = true;
  public showSearchBar!: boolean;
  public productCategories!: CategoriesType[];

  // dependencies
  private readonly _router = inject(Router);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _layoutService = inject(LayoutService);
  private readonly _translateService = inject(TranslateService);
  private readonly _userService = inject(UserService);
  private readonly _authService = inject(AuthService);
  private readonly _dialogService = inject(DialogService);
  private readonly _hasAccessPipe = inject(HasAccessPipe);
  private readonly _categoriesService = inject(CategoriesService);

  constructor() {
    // set advance search visibility state
    this._router.events.subscribe(_ => {
      this.showAdvanceSearch();
    });

    effect(() => {
      this.hiddenMegaMenu = this._layoutService.hiddenMegaMenu();

      this.hiddenAdvancedFilters = this._layoutService.hiddenAdvancedFilters();

      this.hiddenProfileMenu = this._layoutService.hiddenProfileMenu();
    });

    // set selected language
    this.selectedLang = this._translateService.currentLang;
    this._translateService.onLangChange.subscribe(lang => {
      this.selectedLang = lang.lang;
    });

    // set user data
    this._userService.user$.subscribe((user: IUser) => (this.user = user));

    // track router url changes
    this._router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.activeRoute = event.url;
        // show advance filter and search if its the home page or products page
        this.hiddenAdvanceSearch();
        // hidden profile menu on route change
        this._layoutService.hiddenProfileMenu.set(true);
      }
    });

    if (!this._hasAccessPipe.transform(Roles.ViewCustomers)) {
      this.menu = this.menu.filter(
        menuItem => !menuItem.name.includes('customers')
      );
    }

    effect(() => {
      this.productCategories = this._categoriesService.categoriesData();
    });

    afterNextRender(() => {
      if (this.productCategories?.length === 0) {
        // fetch categories data
        this._categoriesService.getCategories();
      }
    });
  }

  ngOnInit(): void {
    this.activeRoute = this._router.url;
  }

  public toggleMegaMenu(menuItem?: string) {
    if (menuItem === '/categories') {
      this._layoutService.hiddenMegaMenu.set(false);
      this._layoutService.windowIsOpen.set(true);
    } else {
      this._layoutService.hiddenMegaMenu.set(true);
      this.hiddenAdvancedFilters &&
        this.hiddenProfileMenu &&
        this._layoutService.windowIsOpen.set(false);
    }
  }

  public toggleAdvancedFilters() {
    this._layoutService.hiddenAdvancedFilters.set(!this.hiddenAdvancedFilters);
    this._layoutService.windowIsOpen.set(!this.hiddenAdvancedFilters);
  }

  public toggleProfileMenu() {
    if (this.user.username) {
      this._layoutService.hiddenProfileMenu.set(false);
      this._layoutService.windowIsOpen.set(true);
    } else {
      const dialogRef = this._dialogService.open(SignupDialogComponent, {
        width: 'none',
        height: 'none',
      });

      dialogRef.afterClosed().subscribe(res => {});
    }
  }

  public closeProfileMenu(e: MouseEvent, headerContent: Element) {
    if (this.hiddenProfileMenu === false && e.target === headerContent) {
      this._layoutService.windowIsOpen.set(false);
      this._layoutService.hiddenAdvancedFilters.set(true);
      this._layoutService.hiddenProfileMenu.set(true);
    }
  }

  public showAdvanceSearch() {
    // check if route data have hiddenTopSearchBar the hidden search bar
    this._activatedRoute.children.map(route => {
      route.data.subscribe(data => {
        if (data?.['hiddenTopSearchBar']) {
          this.showSearchBar = false;
        } else {
          this.showSearchBar = true;
        }
      });
    });
  }

  public hiddenAdvanceSearch() {
    this.showSearchBar = false;
  }

  public setLang(lang: string) {
    this.selectedLang = lang;
    // change language to lowercase
    this._translateService.use(lang.toLowerCase());
    // set profile lang
    if (this.user.username) {
      this._userService.updateProfileLanguage(lang.toLowerCase());
    }
  }

  public openUpgrade() {
    const dialogRef = this._dialogService.open(BusinessAccountDialogComponent, {
      width: '26em',
      height: 'none',
    });

    dialogRef.afterClosed().subscribe(res => {
      if (res) {
        this._userService.changeToBusiness().subscribe(response => {
          if (response && response.status) {
            this._authService.refreshToken(true);
          }
        });
      }
    });
  }
}
